/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useCallback, useEffect, useState, useRef } from "react";
import { useLocation } from 'react-router-dom';
import { useApp } from "../../context/AppContext";
import { Button } from "../../components/ui/button";
import { Loader2, ChevronLeft, ChevronRight, CalendarDays, CalendarPlus, BellRing, FileDown, Send } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { LEAVE_TYPES_EN, inp } from "./hrmsShared";
import { ScheduleGrid, MyWeekCards } from "./ScheduleGrid";
import { AddDutyDialog, DutyDetailDialog } from "./DutyDialog";
import { fmtD } from "../../lib/date";
import { OvertimeRequestDialog, OvertimeDetailDialog, OvertimeInbox } from './OvertimePanel';

const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); x.setHours(0, 0, 0, 0); return x; };
const shift = (iso, days) => { const x = new Date(iso + "T00:00:00"); x.setDate(x.getDate() + days); return isoLocal(x); };

/* 考勤排班（周）：部门经理 / 分管 VP 看本部门周视图并安排值班；mine=true 时为「我的排班」 */
export default function HRMSSchedule({ mine = false }) {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const [start, setStart] = useState(isoLocal(mondayOf(new Date())));
  const [dept, setDept] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [add, setAdd] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");
  const [otContext, setOtContext] = useState(null);
  const [overtime, setOvertime] = useState([]);
  const [otInbox, setOtInbox] = useState([]);
  const [otError, setOtError] = useState('');
  const [otDay, setOtDay] = useState(null);
  const [otDetail, setOtDetail] = useState(null);
  const requestSequence = useRef(0);
  const weekInitialized = useRef(false);
  const location = useLocation();
  // 加班入口按后端返回的权限显示，直属经理即使没有 HR 查看权限也能处理自己的审批。
  const loadOvertime = useCallback(async () => {
    const sequence = ++requestSequence.current;
    try {
      const { data: ctx } = await api.get('/hrms/overtime/context');
      const [{ data: rows }, inbox] = await Promise.all([
        api.get('/hrms/overtime', { params: { start, end: shift(start, 6) } }),
        ctx.canReview ? api.get('/hrms/overtime', { params: { mode: 'review' } }) : Promise.resolve({ data: [] }),
      ]);
      if (sequence !== requestSequence.current) return;
      setOtContext(ctx); setOvertime(rows); setOtInbox(inbox.data.filter(r => r.canReview && (r.status === 'pending' || r.needsReview))); setOtError('');
    } catch (e) { if (sequence === requestSequence.current) setOtError(e?.detailText || (zh ? '加班信息加载失败，请重试' : 'Could not load overtime. Please retry.')); }
  }, [api, start, zh]);
  useEffect(() => { loadOvertime(); const timer = setInterval(loadOvertime, 60000); return () => { clearInterval(timer); requestSequence.current++; }; }, [loadOvertime]);
  // 美国周日和成都周一可能是同一时刻：首次进入个人周历按员工公司日期定位。
  useEffect(() => {
    if (otContext?.today && !weekInitialized.current) {
      weekInitialized.current = true;
      setStart(isoLocal(mondayOf(new Date(otContext.today + 'T12:00:00'))));
    }
  }, [otContext]);
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('overtime');
    if (!id) return;
    let active = true;
    api.get(`/hrms/overtime/${encodeURIComponent(id)}`).then(({ data: r }) => { if (active) setOtDetail(r); }).catch(e => { if (active) setOtError(e?.detailText || '无法打开加班申请'); });
    return () => { active = false; };
  }, [api, location.search]);

  const exportPdf = async () => {
    setBusy("pdf");
    try {
      const res = await api.get("/hrms/schedule/week/pdf", { params: { start, department: dept }, responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `schedule_${data?.department || "me"}_${start}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(zh ? "排班表 PDF 已生成，可打印张贴" : "PDF downloaded");
    } catch { toast.error(zh ? "导出失败" : "Export failed"); } finally { setBusy(""); }
  };
  const checkin = async (d) => {
    try { const { data: r } = await api.post(`/hrms/schedule/duties/${d.id}/checkin`); toast.success(zh ? `值班签到成功 ${r.checkinLocalTime || ""}` : "Checked in"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "签到失败" : "Check-in failed")); }
  };
  const sendDept = async () => {
    if (!window.confirm(zh ? `确认把 ${data?.departmentName || ""} 本周排班表发送给部门全体？（有邮箱的同事收到 PDF 邮件，所有同事收到铃铛提醒）` : "Send this week's schedule to the whole department?")) return;
    setBusy("send");
    try {
      const { data: r } = await api.post("/hrms/schedule/week/send", { start, department: dept });
      toast.success(zh ? `已发送：邮件 ${r.emailed} 人 · 铃铛 ${r.belled} 人（共 ${r.total} 人）` : `Sent: ${r.emailed} emails, ${r.belled} bells`);
    } catch (e) { toast.error(e?.detailText || (zh ? "发送失败" : "Send failed")); } finally { setBusy(""); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await api.get("/hrms/schedule/week", { params: { start, department: dept, mine: mine ? 1 : 0 } }); setData(d); if (!dept && d.department && d.managerView) setDept(d.department); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [api, start, dept, mine]);
  useEffect(() => { load(); }, [load]);

  const thisMonday = isoLocal(mondayOf(otContext?.today ? new Date(otContext.today + 'T12:00:00') : new Date()));
  const managerView = !!data?.managerView;
  const title = mine || !managerView ? (zh ? "我的排班" : "My Schedule") : (zh ? "考勤排班（周）" : "Weekly Schedule");
  const canCancel = (d) => managerView && data.canSchedule && d.department === data.department;
  const isMine = (d) => data?.myEmployeeId && d.employeeId === data.myEmployeeId;

  return (
    <div className="space-y-4" data-testid="hrms-schedule-page" data-mode={mine || !managerView ? "mine" : "manager"}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="hrms-schedule-title"><CalendarDays className="h-6 w-6 text-red-600" />{title}</h1>
          <p className="text-sm text-slate-500">
            {mine || !managerView
              ? (zh ? "查看工作周安排 · 点击日期申请加班 · 批准后专用定位打卡" : "View your workweek · request overtime by date · clock with GPS after approval")
              : (zh ? `${data?.departmentName || ""} · 周一至周日请假与值班；手机选日期查看员工安排，点击「安排值班」或「+」新增。` : `${data?.departmentName || ""} · weekly leave & duties; on mobile, choose a date and use “Schedule duty” or “+” to add.`)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {managerView && !mine && data.departments.length > 1 && (
            <select data-testid="hrms-schedule-dept" className={`${inp} mt-0 w-auto`} value={dept || data.department} onChange={(e) => setDept(e.target.value)}>
              {data.departments.map((d) => <option key={d.id} value={d.id}>{zh ? d.name : d.nameEn}</option>)}
            </select>
          )}
          <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="hrms-schedule-weeknav">
            {/* 手机周切换与按天列表共用同一 start，扩大点击区但不改变公司的日期和时区口径。 */}
            <button type="button" data-testid="hrms-schedule-prev" onClick={() => setStart(shift(start, -7))} className="inline-flex min-h-11 min-w-11 items-center justify-center px-2 py-1.5 hover:bg-slate-50 md:min-h-0 md:min-w-0" aria-label={zh ? "上一周" : "Previous week"} title={zh ? "上一周" : "Prev week"}><ChevronLeft className="h-4 w-4" /></button>
            <button type="button" data-testid="hrms-schedule-this" onClick={() => setStart(thisMonday)} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 min-h-11 px-3 py-1.5 text-sm md:min-h-0 md:text-xs font-semibold border-x border-slate-200 ${start === thisMonday ? "text-red-600" : "text-slate-600 hover:bg-slate-50"}`}>{zh ? "本周" : "This week"}</button>
            <button type="button" data-testid="hrms-schedule-next" onClick={() => setStart(shift(start, 7))} className="inline-flex min-h-11 min-w-11 items-center justify-center px-2 py-1.5 hover:bg-slate-50 md:min-h-0 md:min-w-0" aria-label={zh ? "下一周" : "Next week"} title={zh ? "下一周" : "Next week"}><ChevronRight className="h-4 w-4" /></button>
          </div>
          <span className="text-xs font-semibold text-slate-700" data-testid="hrms-schedule-week-label">{data ? `${fmtD(data.weekStart)} – ${fmtD(data.weekEnd)}` : ""}</span>
          {managerView && !mine && (
            <Button size="sm" variant="outline" data-testid="hrms-schedule-pdf" disabled={!!busy} onClick={exportPdf} className="h-8 text-xs">{busy === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FileDown className="h-3.5 w-3.5 mr-1" />}{zh ? "导出 PDF" : "Export PDF"}</Button>
          )}
          {managerView && !mine && data.canSchedule && (
            <Button size="sm" variant="outline" data-testid="hrms-schedule-send" disabled={!!busy} onClick={sendDept} className="h-8 text-xs border-red-200 text-red-700 hover:bg-red-50">{busy === "send" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}{zh ? "发送到部门" : "Send to dept"}</Button>
          )}
          {managerView && !mine && data.canSchedule && (
            <Button size="sm" data-testid="hrms-schedule-add" onClick={() => setAdd({})} className="bg-brand-500 hover:bg-brand-600 text-white h-8 text-xs"><CalendarPlus className="h-3.5 w-3.5 mr-1" />{zh ? "安排值班" : "Schedule duty"}</Button>
          )}
        </div>
      </div>

      {data?.myPendingDuties > 0 && (
        <div data-testid="hrms-my-pending-banner" className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 flex items-center gap-2">
          <BellRing className="h-4 w-4" />{zh ? `您有 ${data.myPendingDuties} 项值班安排待确认，请点开红色 / 橙色值班块确认。` : `${data.myPendingDuties} duty assignment(s) awaiting your confirmation.`}
        </div>
      )}

      {data && managerView && !mine && data.todayDuties?.length > 0 && (
        <div data-testid="hrms-today-duty-board" className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-semibold text-slate-800">{zh ? "今日值班到岗" : "Today's duty attendance"}</span>
          <span className="text-slate-500">{data.todayDuties.filter((x) => x.checkinAt).length}/{data.todayDuties.length}{zh ? " 已签到" : " checked in"}</span>
          {data.todayDuties.map((x, i) => (
            <span key={`${x.id}-${i}`} data-testid={`hrms-today-duty-${x.id}`} data-checkin={x.checkinAt ? "1" : "0"} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-medium ${x.checkinAt ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${x.checkinAt ? "bg-emerald-500" : "bg-amber-500"}`} />{x.employeeName} · {x.typeName}{x.timeStart ? ` ${x.timeStart}` : ""} · {x.checkinAt ? (zh ? `已到岗 ${x.checkinLocalTime}` : `in ${x.checkinLocalTime}`) : (zh ? "未签到" : "not yet")}
            </span>
          ))}
        </div>
      )}

      {loading && !data && <p className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{zh ? "加载中…" : "Loading…"}</p>}
      {data && !data.hasEmployee && !managerView && (
        <p className="text-sm text-slate-500 rounded-xl border border-slate-200 bg-white p-4" data-testid="hrms-schedule-no-emp">{zh ? "您的账号尚未关联员工档案，请联系 HR。" : "Your account is not linked to an employee record. Contact HR."}</p>
      )}
      {otError && <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{otError}<Button className="ml-2 min-h-11" variant="outline" onClick={loadOvertime}>{zh ? '重试' : 'Retry'}</Button></div>}
      {data && (mine || !managerView) && data.hasEmployee && <MyWeekCards data={data} zh={zh} onDuty={setDetail} onCheckin={checkin} leaveTypesEn={LEAVE_TYPES_EN} overtime={overtime} onOvertime={setOtDetail} today={otContext?.today} onApply={otContext?.employeeId ? setOtDay : undefined} />}
      {otContext?.timeZone && (mine || !managerView) && <p className="text-xs text-slate-500">{zh ? '加班按公司时区记录' : 'Overtime uses company time zone'}：{otContext.timeZone} · {zh ? '批准时长与实际打卡分别统计，异常由经理／HR核对。' : 'Approved and actual hours are tracked separately; exceptions need manager / HR review.'}</p>}
      {otContext?.canReview && <OvertimeInbox rows={otInbox} zh={zh} onOpen={setOtDetail} />}
      {data && managerView && !mine && <ScheduleGrid data={data} zh={zh} onAdd={(row, day) => setAdd({ employeeId: row.employeeId, date: day.date })} onDuty={setDetail} leaveTypesEn={LEAVE_TYPES_EN} />}

      {data && (
        <div className="flex flex-wrap gap-3 text-[11px] text-slate-500" data-testid="hrms-schedule-legend">
          <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-red-600 inline-block" />{zh ? "周末值班" : "Weekend duty"}</span>
          <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-orange-500 inline-block" />{zh ? "营销活动值班" : "Event duty"}</span>
          <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-sky-300 inline-block" />{zh ? "已批准请假（虚线 = 待审批）" : "Approved leave (dashed = pending)"}</span>
          <span>✓ {zh ? "已确认" : "confirmed"} · ◔ {zh ? "待确认" : "pending"} · {zh ? `工作时间 ${data.workStart}-${data.workEnd}` : `Hours ${data.workStart}-${data.workEnd}`}</span>
        </div>
      )}

      {add && data && <AddDutyDialog zh={zh} api={api} data={data} preset={add} onClose={() => setAdd(null)} onSaved={() => { setAdd(null); load(); }} />}
      {detail && <DutyDetailDialog zh={zh} api={api} duty={detail} canCancel={canCancel(detail)} isMine={isMine(detail)} today={data?.today} onClose={() => setDetail(null)} onChanged={load} />}
      {otDay && otContext && <OvertimeRequestDialog api={api} zh={zh} day={otDay} context={otContext} onClose={() => setOtDay(null)} onSaved={r => { setOtDay(null); setOtDetail(r); loadOvertime(); }} />}
      {otDetail && <OvertimeDetailDialog key={otDetail.id} api={api} zh={zh} row={otDetail} onClose={() => setOtDetail(null)} onChanged={loadOvertime} />}
    </div>
  );
}
